import { EventEmitter, Input, OnInit, OnChanges, Directive } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { DataField } from './models/datafield';
import { DataFieldTypes } from './models';

@Directive()
export abstract class DataFieldComponentBase implements OnInit, OnChanges {

    /**
    * Event to notify when the component enters the edit-mode
    */
    public editMode: EventEmitter<void> = new EventEmitter<void>();

    /**
    * Event to notify when the component has been committed
    */
    public commitMode: EventEmitter<void> = new EventEmitter<void>();

    /**
    * Event to notify when the component has been cancelled from edit-mode
    */
    public cancelMode: EventEmitter<void> = new EventEmitter<void>();

    /**
     * Event to notify that the value of a datafield has changed.
     */
    public valueChanged: EventEmitter<DataField> = new EventEmitter<DataField>();

    /**
     * Protected contructor to prevent instances of this class being created directly.
     */
    protected constructor() { }

    ngOnInit(): void {
        if (this.isAlwaysEditable) {
            this.edit();
        }
    }

    ngOnChanges(changes: any) {
        if (changes.datafield) {
            this.initDataField();
        }
    }

    isDropDown() {
        return this.datafield?.DataType?.DataTypeEnum === DataFieldTypes.DropDown || this.datafield?.DataType?.DataTypeName === 'DropDown';
    }

    isOptionalDropDown() {
        return this.isDropDown() && !this.datafield.IsRequired;
    }

    initDataField() {
        // If the datafield doesn't contains a value - set the default value; except for optional dropdowns (enums)
        if (this.datafield && this.datafield.Value === null && !this.isOptionalDropDown()) {
            this.datafield.Value = this.datafield.DefaultValue;
        }
    }

    notifyValueChanged(datafield: DataField) {
        if (this.valueChanged) {
            this.valueChanged.emit(datafield);
        }
    }

    /**
     * Makes the component editable
     */
    public edit() {
        if (!this.datafield) {
            throw Error("No valid datafield provided for this datafield-component. Commit cannot be called! Use the setDataField method.");
        }
        this.backupValue = this.datafield.Value;
        this.backupSecretId = this.datafield.SecretId;
        this.isEditable = true;
        this.editMode.emit();
    }

    /**
     * Commits the changes to the datasource
     */
    public commit() {
        if (!this.datafield) {
            throw Error("No valid datafield provided for this datafield-component. Commit cannot be called! Use the setDataField method.");
        }
        this.backupValue = this.datafield.Value;
        this.backupSecretId = this.datafield.SecretId;
        this.isEditable = false;
        this.commitMode.emit();
    }

    /**
     * Cancels the edit-mode and revert the value to it's previous state.
     */
    public cancel() {
        if (this.isEditable) {
            this.datafield.Value = this.backupValue;
            this.datafield.SecretId = this.backupSecretId;
            this.isEditable = false;
            this.cancelMode.emit();
        }
    }

    /**
    *  This is the backup value used when cancel has been called
    */
    public backupValue: string;
    public backupSecretId: string;
    public isEditable: boolean;

    @Input() public formValidationGroup: UntypedFormGroup;

    @Input() public isAlwaysEditable: boolean; // Decorators like @Input() can't be inherited - therefore we need to force the childs to implement them.
    /**
    * The datafield to be represented by this component
    */
    @Input() public datafield: DataField;

    /**
    * The prefix used for identifiers (like formControlName) when using validation.
    */
    static get VALIDATION_FIELD_PREFIX() {
        return 'df_';
    }

    static generateIdentifier(datafield: DataField) {
        return (DataFieldComponentBase.VALIDATION_FIELD_PREFIX + '_' + datafield.UniqueId + '_' + datafield.DataFieldId + '_' + datafield.DisplayName.replace(' ', '_')).slice(0, 30); // Include display name for easier debugging...
    }

    /**
    * Gets the identifier which should be used when validating the dynamic datafields.
    */
    get validationIdentifier() {
        if (this.datafield) {
            return DataFieldComponentBase.generateIdentifier(this.datafield);
        }
        else {
            return undefined;
        }
    }
}
